import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';

/**
 * PaymentPlan — INT-0018 card installments (spec 2026-09-10).
 * One document per accepted plan: the registrant chose "N monthly payments"
 * at checkout; installment 1 was collected by the normal checkout flow with
 * the card saved for off-session use; the api's hourly cron charges the
 * remainder. Amount math lives in utils/installments.ts (shared with the
 * funnels + admin so all three agree to the cent).
 *
 * Status: 'active' (charges pending) → 'completed' | 'overdue' (retries
 * exhausted — admin flagged, SEAT KEPT per the owner decision) |
 * 'canceled' (admin refund/cancel — remaining charges stopped).
 */
export const PAYMENT_PLAN_STATUSES = ['active', 'completed', 'overdue', 'canceled'] as const;

export const PaymentPlanSchema = z.object({
  _id: objectId,
  organization: objectId,
  registration: objectId,
  registrationTeam: objectId,
  /** Set for per-player (player-pays-separately) plans; absent = team plan. */
  player: objectId.optional(),
  paymentProduct: objectId.optional(),

  /** Full price of the underlying fee in cents (what pay-in-full would be). */
  baseCents: z.number(),
  /** base + (count−1)×60¢ — see utils/installments.ts. */
  totalCents: z.number(),
  /** Per-installment cents, length === count, remainder front-loaded. */
  installmentsCents: z.array(z.number()),
  count: z.number(),

  /** Installments successfully charged so far (1 after checkout). */
  chargedCount: z.number(),
  /** When the next off-session charge is due (absent when terminal). */
  nextChargeAt: z.date().optional(),
  /** Day-of-month anchor from the checkout date (clamped in short months). */
  anchorDay: z.number(),

  status: z.enum(PAYMENT_PLAN_STATUSES),
  /** Off-session retry count for the CURRENT installment (reset on success). */
  retryCount: z.number().optional(),
  /** Last Stripe decline/error message, for the admin flag. */
  lastError: z.string().optional(),

  /** Stripe references — customer/payment-method live on the CONNECTED account. */
  stripeCustomerId: z.string().optional(),
  stripePaymentMethodId: z.string().optional(),
  stripeAccountId: z.string().optional(),
  /** Checkout session that created the plan — webhook idempotency key. */
  stripeCheckoutSessionId: z.string().optional(),

  ...timestampFields,
});

export type PaymentPlan = z.infer<typeof PaymentPlanSchema>;

export const paymentPlanMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  organization: { label: 'Organization', ref: 'Organization' },
  registration: { label: 'Registration', ref: 'Registration' },
  registrationTeam: { label: 'Registration Team', ref: 'RegistrationTeam' },
  player: { label: 'Player', ref: 'Player' },
  paymentProduct: { label: 'Payment Product', ref: 'PaymentProduct' },
  baseCents: { label: 'Base (cents)' },
  totalCents: { label: 'Plan total (cents)' },
  installmentsCents: { label: 'Installments (cents)' },
  count: { label: 'Installment count' },
  chargedCount: { label: 'Charged so far' },
  nextChargeAt: { label: 'Next charge' },
  anchorDay: { label: 'Anchor day of month' },
  status: { label: 'Status' },
  retryCount: { label: 'Retry count' },
  lastError: { label: 'Last error' },
  stripeCustomerId: { label: 'Stripe customer', hidden: true },
  stripePaymentMethodId: { label: 'Stripe payment method', hidden: true },
  stripeAccountId: { label: 'Stripe account', hidden: true },
  stripeCheckoutSessionId: { label: 'Origin checkout session', hidden: true },
};
